function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((done, fail) => {
        jQuery.getScript('//web.infrakit.com/lib/filesaver_plus_toblob.js', () => {
            canvas.toBlob(blob => {
                done(blob);
            });
        });
    });
}

async function saveToFile(canvas: HTMLCanvasElement, filename: string, format: 'png' | 'jpeg' | 'pdf'): Promise<void> {
    const fullname = filename + '.' + format;

    // png packed inside pdf is 10x the size of just png, so always use jpegs inside pdf
    const convertedCanvas = await toBase64(canvas, -1, format === 'pdf' ? 'jpeg' : format, 0.95);

    if (format !== 'pdf') {
        (window as any).saveAs(convertedCanvas, fullname)
    } else {
        return new Promise((done, fail) => {
            jQuery.getScript('//web.infrakit.com/lib/jsPDF-2.5.1/jspdf.umd.min.js', async () => {
                const { jsPDF } = (window as any).jspdf;
                const pdf = new jsPDF({
                    orientation: canvas.width > canvas.height ? 'l' : 'p',
                    unit: 'px',
                    format: [canvas.width, canvas.height]
                });
                pdf.addImage(convertedCanvas, 'jpeg', 0, 0, canvas.width, canvas.height);
                done();
                pdf.save(fullname);
            });
        });
    }
}

async function toBase64(canvas: HTMLCanvasElement, width = -1, format: 'png' | 'jpeg' = 'jpeg', quality = 0.7): Promise<string> {
    const resized = await resizeCanvas(canvas, width);
    return format === 'png' ? resized.toDataURL(`image/${format}`) : resized.toDataURL(`image/${format}`, quality);
}

async function resizeCanvas(canvas: HTMLCanvasElement, maxWidth: number): Promise<HTMLCanvasElement> {
    if (maxWidth < 10) return Promise.resolve(canvas);

    const img = await makeImage(canvas.toDataURL());

    const resizedCanvas = document.createElement('canvas');
    let width = img.width;
    let height = img.height;

    if (width > maxWidth) {
        height *= maxWidth / width;
        width = maxWidth;
    }

    resizedCanvas.width = width;
    resizedCanvas.height = height;

    const ctx = resizedCanvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    return resizedCanvas;
}

async function makeImage(src: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((done, fail) => {
        const img = new Image();
        img.onload = () => {
            done(img);
        };
        img.src = src;
    });
}

const toGrayscale = async (base64Image: string): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = await makeImage(base64Image);

    canvas.width = img.width;
    canvas.height = img.height;

    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const gray = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
        data[i] = data[i + 1] = data[i + 2] = gray;
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
};

const svgToPng = async (svgBase64: string, scale = 1): Promise<string> => {
    if (!svgBase64.startsWith('data:image')) {
        svgBase64 = 'data:image/svg+xml;base64,' + btoa(svgBase64);
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = await makeImage(svgBase64);

    canvas.width = img.width * scale;
    canvas.height = img.height * scale;

    if (scale !== 1) {
        ctx.scale(scale, scale);
    }

    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
};

function rotateCanvas(canvas: HTMLCanvasElement, angleDeg: number): HTMLCanvasElement {
    const angleRad = Math.abs(angleDeg * Math.PI / 180);
    const cosAngle = Math.cos(angleRad);
    const sinAngle = Math.sin(angleRad);

    const newWidth = Math.ceil(Math.abs(canvas.width * cosAngle) + Math.abs(canvas.height * sinAngle));
    const newHeight = Math.ceil(Math.abs(canvas.width * sinAngle) + Math.abs(canvas.height * cosAngle));

    const newCanvas = document.createElement('canvas');
    newCanvas.width = newWidth;
    newCanvas.height = newHeight;
    const newCtx = newCanvas.getContext('2d');

    newCtx.translate(newWidth / 2, newHeight / 2);
    newCtx.rotate(angleRad);
    newCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height);

    return newCanvas;
}

function trimEmptySpace(c: HTMLCanvasElement): {
    canvas: HTMLCanvasElement;
    bound: { top: number; left: number; bottom: number; right: number };
    oldHeight: number; oldWidth: number,
    newHeight: number; newWidth: number,
} {
    const ctx = c.getContext('2d');
    const copy = document.createElement('canvas').getContext('2d');
    const pixels = ctx.getImageData(0, 0, c.width, c.height);
    const l = pixels.data.length;
    let i;
    const bound = {
        top: null,
        left: null,
        right: null,
        bottom: null
    };
    let x;
    let y;

    // Iterate over every pixel to find the highest
    // and where it ends on every axis ()
    for (i = 0; i < l; i += 4) {
        if (pixels.data[i + 3] !== 0) {
            x = (i / 4) % c.width;
            y = ~~((i / 4) / c.width);

            if (bound.top === null) {
                bound.top = y;
            }

            if (bound.left === null) {
                bound.left = x;
            }
            else if (x < bound.left) {
                bound.left = x;
            }

            if (bound.right === null) {
                bound.right = x;
            }
            else if (bound.right < x) {
                bound.right = x;
            }

            if (bound.bottom === null) {
                bound.bottom = y;
            }
            else if (bound.bottom < y) {
                bound.bottom = y;
            }
        }
    }

    const safeMargin = 3;
    bound.bottom += safeMargin;
    bound.top -= safeMargin;
    bound.right += safeMargin;
    bound.left -= safeMargin;

    // Calculate the height and width of the content
    const trimHeight = bound.bottom - bound.top;
    const trimWidth = bound.right - bound.left;
    const trimmed = ctx.getImageData(bound.left, bound.top, trimWidth, trimHeight);

    copy.canvas.width = trimWidth;
    copy.canvas.height = trimHeight;
    copy.putImageData(trimmed, 0, 0);

    // Return trimmed canvas
    return {
        bound,
        oldWidth: c.width,
        oldHeight: c.height,
        newWidth: trimWidth,
        newHeight: trimHeight,
        canvas: copy.canvas
    };
}

function isCanvasTainted(canvas: HTMLCanvasElement) {
    try {
        canvas.toDataURL();
        return false;
    } catch (e) {
        console.error(e);
        return true;
    }
}

export const CanvasUtils = {
    toBlob,
    saveToFile,
    toBase64,
    makeImage,
    trimEmptySpace,
    toGrayscale,
    isCanvasTainted,
    svgToPng,
    rotateCanvas
};
